<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import InputBase from '../components/InputBase.vue';
import ButtonBase from '../components/ButtonBase.vue';
import CryptoJS from 'crypto-js';
import { useRouter } from 'vue-router';
import Swal from 'sweetalert2';

const router = useRouter();
const isLoading = ref(false);

const validaCaptcha = ref(false);
const siteKey = ref('6LeqiTAqAAAAAD1vo2jteRQvPmo1QvWaF-M493Me');
const dato = ref('');
const tipo_busqueda = ref(1);
const url = process.env.VUE_APP_API_URL;
const apikey = process.env.VUE_APP_SECRET_KEY;
const apiNode = process.env.VUE_APP_API_NODE;

// Configuración
const KEY = process.env.VUE_APP_CRYPT; // Debe coincidir con el KEY en Java
const SALT = process.env.VUE_APP_CRYPT; // Debe coincidir con el SALT en Java
const ITERATION_COUNT = 65536; // Debe coincidir con el ITERATION_COUNT en Java
const KEY_LENGTH = 256; // Debe coincidir con el KEY_LENGTH en Java

const encrypt = (strToEncrypt) => {
    try {
        const saltBytes = CryptoJS.enc.Utf8.parse(SALT);
        const iv = CryptoJS.lib.WordArray.random(16); // Genera un IV de 16 bytes
        const key = CryptoJS.PBKDF2(KEY, saltBytes, {
            keySize: KEY_LENGTH / 32,
            iterations: ITERATION_COUNT,
        });

        const encrypted = CryptoJS.AES.encrypt(strToEncrypt, key, {
            iv: iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
        });

        // Combina IV y el texto cifrado
        const combined = iv.concat(encrypted.ciphertext);
        return CryptoJS.enc.Base64.stringify(combined);
    } catch (error) {
        console.error('Error al encriptar:', error);
        return null;
    }
};


const enviarDatos = async () => {
  isLoading.value = true;
  try {
    const token = grecaptcha.getResponse();
    if (!token) {
      Swal.fire("Error", 'Captcha no verificado', "error");
      isLoading.value = false;
      return;
    }

    const result = await axios.post(apiNode, { response: token });
    //console.log('Respuesta del captcha:', result.data);
    
    if (result.data.success) {
      validaCaptcha.value = true;
      // Reset del captcha después de uso exitoso
      grecaptcha.reset();
    } else {
      validaCaptcha.value = false;
      // Reset del captcha si falla la verificación
      grecaptcha.reset();
      Swal.fire("Error", 'Error en la verificación del captcha. Por favor, inténtalo de nuevo.', "error");
      isLoading.value = false;
      return;
    }

    if (validaCaptcha.value) {
      let id;
      if (tipo_busqueda.value === 1) {
        if(dato.value.length !=18){
          Swal.fire("Error", 'Escribiste una CURP diferente de 18 caracteres', "warning");
          grecaptcha.reset();
          isLoading.value = false;
          return;
        }
        id = await buscaPorCurp(dato.value);
      } else if (tipo_busqueda.value === 0) {
        if(isNaN(Number(dato.value))){
          Swal.fire("Error", 'Debes de escribir un número', "warning");
          grecaptcha.reset();
          isLoading.value = false;
          return;
        }
        if(Number(dato.value)>9999999 || Number(dato.value)<=0){
          Swal.fire("Error", 'Escribe un id correcto', "warning");
          grecaptcha.reset();
          isLoading.value = false;
          return;
        }

        id = await buscaPorExpediente(dato.value);
      }
      if (id != null) {
        router.push({ name: 'historiaOperador', query: { id } });
      } else {
        Swal.fire("Alerta", 'Los datos no arrojaron registros', "warning");
        grecaptcha.reset();
      }
    } else {
      Swal.fire("Error", 'Captcha no verificado', "error");
      grecaptcha.reset();
    }
  } catch (error) {
    console.error('Error al enviar datos', error);
    grecaptcha.reset();
    Swal.fire("Error", 'Error en el proceso de búsqueda. Por favor, inténtalo de nuevo.', "error");
  }finally{
    isLoading.value = false;
  }
};

const buscaPorCurp = async (curp) => {
  const ruta = `/personaCita/getByCURP/${curp}`;
  try {
    const response = await axios.get(`${url}${ruta}`, {
      headers: {
        'api-key': apikey
      }
    });
    if (response.data.status === "OK" && response.data.data != null) {
      let id = response.data.data[0].personaId;  // Accedes al personaId
      //console.log(id);  // Imprime la respuesta completa para debug
      let dataEncriptada = encrypt(id.toString());  // Encriptas el id
      //console.log(dataEncriptada);  // Muestra la data encriptada
      return dataEncriptada;  // Retornas la data encriptada
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error al consumir servicio', ruta, error);
    return null;
  }
};

const buscaPorExpediente = async (expedienteId) => {
  const ruta = `/personaCita/obtener-nombre/${expedienteId}`;
  try {
    const response = await axios.get(`${url}${ruta}`, {
      headers: {
        'api-key': apikey
      }
    });
    if (response.data.status === "OK" && response.data.data != null) {
      let id = response.data.data.personaId;  // Accedes al personaId
      //console.log(id);  // Imprime la respuesta completa para debug
      let dataEncriptada = encrypt(id.toString());  // Encriptas el id
      //console.log(dataEncriptada);
      return dataEncriptada;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error al consumir servicio', ruta, error);
    return null;
  }
};


const loadRecaptcha = () => {
  // Limpiar cualquier captcha existente primero
  const recaptchaElement = document.getElementById('g-recaptcha');
  if (recaptchaElement) {
    recaptchaElement.innerHTML = '';
  }

  // Verificar que reCAPTCHA esté completamente cargado
  if (typeof grecaptcha !== 'undefined' && grecaptcha.render && window.recaptchaLoaded) {
    try {
      // Resetear cualquier captcha existente
      if (typeof grecaptcha.reset === 'function') {
        try {
          grecaptcha.reset();
        } catch (e) {
          //console.log('No hay captcha previo para resetear');
        }
      }
      
      // Renderizar nuevo captcha
      setTimeout(() => {
        if (recaptchaElement && !recaptchaElement.hasChildNodes()) {
          grecaptcha.render('g-recaptcha', {
            sitekey: siteKey.value,
            callback: (response) => {
              //console.log('Captcha resuelto:', response);
            },
            'expired-callback': () => {
              //console.log('Captcha expirado, recargando...');
              loadRecaptcha();
            },
            'error-callback': () => {
              //console.log('Error en captcha, recargando...');
              setTimeout(loadRecaptcha, 1000);
            }
          });
        }
      }, 100);
    } catch (error) {
      console.error('Error al renderizar captcha:', error);
      setTimeout(loadRecaptcha, 1000);
    }
  } else {
    //console.log('Esperando a que se cargue reCAPTCHA completamente...');
    setTimeout(loadRecaptcha, 500);
  }
};

// Función para limpiar y recargar captcha
const reloadCaptcha = () => {
  validaCaptcha.value = false;
  loadRecaptcha();
};


onMounted(() => {
  validaCaptcha.value = false;
  // Limpiar cualquier estado previo del captcha
  if (typeof grecaptcha !== 'undefined') {
    try {
      grecaptcha.reset();
    } catch (e) {
      //console.log('No hay captcha previo para resetear en mount');
    }
  }
  // Cargar captcha con delay para asegurar que la página esté completamente cargada
  setTimeout(() => {
    loadRecaptcha();
  }, 200);
});
</script>


<template>
  <div class="container mx-auto mb-10 mt-10">
    <h2 class="text-2xl md:text-3xl text-center">Dirección General de Medicina en el Transporte</h2>
    <h3 class="text-xl md:text-2xl mt-4 text-center">.: Consulta de Historial de Exámenes :.</h3>
    <form @submit.prevent="enviarDatos">
      <div class="grid bg_secundarioGrisClaro rounded-lg max-w-xl mx-auto content-center">
        <div class="grid grid-cols-1">
          <div class="flex space-x-5 justify-center">
            <div class="mr-5">
              <label>
                <input type="radio" name="curp" :value="1" v-model="tipo_busqueda"/> POR CURP
              </label>
            </div>
            <div class="mr-5">
              <label>
                <input type="radio" name="id" :value="0" v-model="tipo_busqueda"/> POR ID MÉDICO
              </label>
            </div>
          </div>
          <div class="mt-6 mb-6">
            <input-base label="Ingresa información" v-model="dato"></input-base>
          </div>
          <div class="mt-6 mb-6">
            <div id="g-recaptcha" class="g-recaptcha" :data-sitekey="siteKey"></div>
            <div class="flex justify-center mt-2">
              <button 
                type="button"
                @click="reloadCaptcha"
                class="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                ¿Problemas con el captcha? Hacer clic para recargar
              </button>
            </div>
          </div>
          <div v-if="!isLoading" class="flex justify-center items-center">
            <button-base type='submit' label="Buscar"></button-base>
          </div>
          <div v-else class="flex justify-center items-center" >
            <div class="loader"></div>
          </div>
          
        </div>
      </div>
    </form>
  </div>
</template>

<style scoped>
.loader {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #3498db;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>